import { useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Loader2, Plus, Pencil, Trash2, Save, X, RefreshCw, Rocket } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import {
  catalogStatus, catalogList, catalogUpsert, catalogDelete,
  catalogPickerMedications, catalogPickerIngredients,
} from "../lib/api";
import {
  SLUGS, CATALOG_META, DEFAULTS,
} from "../components/admin/catalogMeta";
import CatalogForm from "../components/admin/CatalogForm";
import ErrorBoundary from "../components/admin/ErrorBoundary";
import LaunchDrawer from "../components/admin/LaunchDrawer";

const LAUNCH_BADGE = {
  draft:     "bg-stone-100 text-stone-500 border-stone-300",
  in_review: "bg-amber-100 text-amber-700 border-amber-300",
  launched:  "bg-emerald-100 text-emerald-700 border-emerald-300",
};

export default function AdminCatalog() {
  const { ready } = useAuth();

  const [status, setStatus] = useState(null);
  const [active, setActive] = useState("medications");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // current form payload, or null
  const [launchFor, setLaunchFor] = useState(null);  // medication row for launch drawer
  const [pickers, setPickers] = useState({ medications: [], ingredients: [] });

  // ---- guard ----
  // Outer <RequireAdmin> wrapper already enforces auth; this just kicks
  // off the initial data fetch once on mount.
  useEffect(() => {
    if (!ready) return;
    refreshAll();
    refreshPickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // ---- data ops ----
  async function refreshAll() {
    try {
      const s = await catalogStatus();
      setStatus(s);
    } catch (e) { /* noop — handled by next call */ }
    await refreshList(active, q);
  }

  async function refreshList(slug, query) {
    setLoading(true);
    try {
      const data = await catalogList(slug, { q: query });
      setRows(data);
    } catch (e) {
      toast.error(`Failed to load ${slug}: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }

  async function refreshPickers() {
    try {
      const [m, i] = await Promise.all([
        catalogPickerMedications(), catalogPickerIngredients(),
      ]);
      setPickers({ medications: m, ingredients: i });
    } catch (e) { /* graceful */ }
  }

  // ---- tab + search effects ----
  useEffect(() => {
    // Clear stale rows immediately when the active tab changes so the
    // renderer never gets a previous-tab row under the new meta (would
    // crash on missing fields like medication_id.slice(...)).
    setRows([]);
    refreshList(active, q);
    // eslint-disable-next-line
  }, [active]);
  useEffect(() => {
    const t = setTimeout(() => refreshList(active, q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // ---- save / delete ----
  async function onSave() {
    try {
      const r = await catalogUpsert(active, editing);
      toast.success(`${CATALOG_META[active].label}: ${r.action}`);
      setEditing(null);
      refreshAll();
      if (active === "medications" || active === "ingredients") refreshPickers();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onDelete(id, hard = false) {
    if (!window.confirm(hard ? `Permanently delete ${id}?` : `Deactivate ${id}?`)) return;
    try {
      const r = await catalogDelete(active, id, { hard });
      toast.success(`${id}: ${r.action}`);
      refreshAll();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  const meta = CATALOG_META[active];
  const counts = useMemo(() => status?.[active] || {}, [status, active]);

  if (!ready) return null;

  return (
    <main className="min-h-screen bg-[#fdf8f3] px-6 py-12" data-testid="admin-catalog">
      <div className="mx-auto max-w-6xl">
        <AdminHeader />
        <header className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
              Medication catalogue
            </h1>
            <p className="mt-1 text-sm text-stone-600">
              Manage the production schema — medications, ingredients,
              interactions, and products.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={refreshAll}
            data-testid="catalog-refresh">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </header>

        {/* Status strip */}
        {status && (
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {SLUGS.map((s) => (
              <button key={s} onClick={() => setActive(s)}
                data-testid={`stat-card-${s}`}
                className={`rounded-xl border p-4 text-left transition ${
                  active === s
                    ? "border-stone-900 bg-white shadow-sm"
                    : "border-stone-200 bg-white/60 hover:border-stone-400"
                }`}>
                <p className="text-[11px] uppercase tracking-wider text-stone-500">
                  {CATALOG_META[s].label}
                </p>
                <p className="mt-2 text-2xl font-semibold text-stone-900">
                  {status[s]?.total ?? 0}
                </p>
                <p className="mt-0.5 text-[11px] text-stone-500">
                  {status[s]?.active ?? 0} active · {status[s]?.verified ?? 0} verified
                </p>
              </button>
            ))}
          </div>
        )}

        <Tabs value={active} onValueChange={setActive}>
          <TabsList className="mb-4">
            {SLUGS.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>
                {CATALOG_META[s].label}
              </TabsTrigger>
            ))}
          </TabsList>

          {SLUGS.map((s) => (
            <TabsContent key={s} value={s}>
              <ErrorBoundary>
              <div className="mb-4 flex items-center gap-3">
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={meta.searchPlaceholder}
                  className="max-w-md" data-testid="catalog-search" />
                <span className="text-xs text-stone-500">
                  {rows.length} row{rows.length === 1 ? "" : "s"}
                </span>
                <div className="ml-auto" />
                <Button onClick={() => setEditing(DEFAULTS[s]())}
                  data-testid="catalog-new-btn">
                  <Plus className="mr-2 h-4 w-4" /> New {meta.label.toLowerCase()}
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center gap-2 text-stone-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-stone-300 bg-white p-12 text-center text-stone-500">
                  No {meta.label.toLowerCase()} yet. Click "New" to add one.
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                  <ul className="divide-y divide-stone-200">
                    {rows.map((r) => (
                      <li key={r.id} className="flex items-center gap-4 p-4"
                          data-testid={`catalog-row-${r.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-stone-900">
                            {meta.primary(r, pickers)}
                          </p>
                          <p className="truncate text-xs text-stone-500">
                            {meta.secondary(r, pickers)}
                          </p>
                          <p className="mt-1 truncate text-[10px] font-mono text-stone-400">
                            {r.id}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {active === "medications" && (
                            <>
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${LAUNCH_BADGE[r.launch_status || "draft"]}`}
                                data-testid={`launch-badge-${r.id}`}>
                                {(r.launch_status || "draft").replace("_", " ")}
                              </span>
                              <Button variant="ghost" size="icon"
                                onClick={() => setLaunchFor(r)}
                                data-testid={`launch-ctl-${r.id}`}
                                title="Launch control">
                                <Rocket className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon"
                            onClick={() => setEditing({ ...r })}
                            data-testid={`edit-${r.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon"
                            onClick={() => onDelete(r.id, false)}
                            data-testid={`deactivate-${r.id}`}
                            title="Deactivate (soft)">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              </ErrorBoundary>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit drawer (overlay) */}
        {editing && (
          <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40"
               data-testid="catalog-form-overlay" onClick={() => setEditing(null)}>
            <div className="w-full max-w-2xl overflow-y-auto bg-[#fdf8f3] p-8 shadow-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-stone-900">
                  {editing.id ? "Edit" : "New"} {meta.label.slice(0, -1)}
                </h2>
                <Button variant="ghost" size="icon" onClick={() => setEditing(null)}
                  data-testid="catalog-form-close">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <CatalogForm slug={active} value={editing} onChange={setEditing}
                mediaPickers={pickers} />

              <div className="mt-8 flex items-center gap-3">
                <Button onClick={onSave} data-testid="catalog-form-save">
                  <Save className="mr-2 h-4 w-4" /> Save
                </Button>
                <Button variant="ghost" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {launchFor && (
          <LaunchDrawer
            med={launchFor}
            onClose={() => setLaunchFor(null)}
            onChanged={() => refreshList(active, q)}
          />
        )}
      </div>
    </main>
  );
}
